// 261010 UI2 — bốn lỗ sửa tay L6 (THUẦN, không antd): cắt vào/ra · tải clip lên · video tham chiếu @Video · gỡ dấu sửa tay diễn xuất.
// Luật khớp BE (`validateTrim`, `adminFilmEventVideoRefService`, `adminFilmActingClearService`): FE chặn sớm cho đỡ lượt gọi hỏng, BE vẫn là người quyết.
import type { ActingState } from '../../types/filmActing';
import type { FilmEvent } from '../../types/film';
import { apiErrorText } from './apiErrorText';

export const MIN_TRIM_SPAN_S = 0.1;
export interface VideoRefView { key: string; url: string; durationS: number }

const numOf = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') { const t = v.trim().replace(',', '.'); return t !== '' && Number.isFinite(Number(t)) ? Number(t) : null; }
  return null;
};
const blank = (v: unknown): boolean => v === '' || (typeof v === 'string' && v.trim() === '');
const vn = (n: number): string => (Number.isInteger(n) ? String(n) : (Math.round(n * 10) / 10).toFixed(1).replace('.', ','));

/** Body `PUT /clips/:id/trim`. Hai ô trống ⇒ null/null (bỏ cắt). */
export function trimBodyOf(inS: unknown, outS: unknown, durationS: number | null | undefined): { ok: true; cutInS: number | null; cutOutS: number | null } | { ok: false; message: string } {
  if (blank(inS) && blank(outS)) return { ok: true, cutInS: null, cutOutS: null };
  const a = numOf(inS); const b = numOf(outS);
  if (a === null || b === null) return { ok: false, message: 'Điểm vào / ra phải là số (giây).' };
  if (typeof durationS !== 'number' || !Number.isFinite(durationS) || durationS <= 0) return { ok: false, message: 'Clip chưa có thời lượng — không kiểm được điểm cắt.' };
  if (a < 0) return { ok: false, message: 'Điểm vào phải ≥ 0.' };
  if (b > durationS + 1e-9) return { ok: false, message: `Điểm ra vượt thời lượng clip (${vn(durationS)} s).` };
  if (b - a < MIN_TRIM_SPAN_S - 1e-9) return { ok: false, message: `Đoạn giữ lại tối thiểu ${vn(MIN_TRIM_SPAN_S)} s (điểm ra phải sau điểm vào).` };
  return { ok: true, cutInS: a, cutOutS: b };
}

export function trimSummaryOf(durationS: number | null | undefined, cutInS: number | null | undefined, cutOutS: number | null | undefined): string {
  if (typeof durationS !== 'number' || !(durationS > 0)) return '';
  const a = typeof cutInS === 'number' ? cutInS : 0;
  const b = typeof cutOutS === 'number' ? cutOutS : durationS;
  if (a <= 0 && b >= durationS) return `nguyên clip ${vn(durationS)} s`;
  return `giữ ${vn(b - a)} s (cắt ${vn(a)} s đầu · ${vn(Math.max(0, durationS - b))} s đuôi)`;
}

/** File chọn để tải lên làm clip / video tham chiếu: chỉ mp4, có dung lượng. (Thời lượng / fps do BE kiểm.) */
export function clipFileCheck(f: { name?: string; type?: string; size?: number } | null | undefined): { ok: true } | { ok: false; message: string } {
  if (!f) return { ok: false, message: 'Chưa chọn file.' };
  const mp4 = /\.mp4$/i.test(String(f.name ?? '')) || f.type === 'video/mp4';
  if (!mp4) return { ok: false, message: 'Chỉ nhận file mp4.' };
  if (!(Number(f.size) > 0)) return { ok: false, message: 'File rỗng.' };
  return { ok: true };
}

export function videoRefOf(ev: Pick<FilmEvent, 'videoMeta'> | null | undefined): VideoRefView | null {
  let m: any = ev?.videoMeta;
  if (typeof m === 'string') { try { m = JSON.parse(m); } catch { return null; } }
  const r = m && typeof m === 'object' ? m.workflowVideoRef : null;
  if (!r || typeof r.key !== 'string' || typeof r.url !== 'string' || !r.key || !r.url) return null;
  const d = Number(r.durationS);
  return { key: r.key, url: r.url, durationS: Number.isFinite(d) && d > 0 ? d : 0 };
}

export function videoRefGateOf(ev: Pick<FilmEvent, 'videoEngine'> | null | undefined): { ok: true } | { ok: false; reason: string } {
  return ev?.videoEngine === 'SEEDANCE_25' ? { ok: true } : { ok: false, reason: 'Video tham chiếu chỉ dùng được với Seedance 2.5 — đổi engine của video này sang 2.5 trước.' };
}

export const videoRefTokenOf = (ref: Pick<VideoRefView, 'key'> | null | undefined): string => (ref?.key ? `@[Video tham chiếu](video:${ref.key})` : '');

/** Gỡ dấu "sửa tay" diễn xuất của video (cả video). CAS bằng `manualHash` của `GET …/acting`; BE cũ không trả ⇒ không gỡ được. */
export function clearManualPlanOf(st: ActingState | null | undefined): { ok: true; body: { baseHash: string; shotId?: string; fields?: string[] }; shots: number } | { ok: false; reason: string } {
  if (!st) return { ok: false, reason: 'Chưa đọc được diễn xuất của video.' };
  const hash = (st as any).manualHash;
  if (typeof hash !== 'string' || !/^[0-9a-f]{64}$/.test(hash)) return { ok: false, reason: 'Máy chủ chưa hỗ trợ gỡ dấu sửa tay (thiếu mã đối chiếu).' };
  if (!st.protected || !(st.manualShots ?? []).length) return { ok: false, reason: 'Video này không có dấu sửa tay để gỡ.' };
  return { ok: true, body: { baseHash: hash }, shots: st.manualShots.length };
}

const CODE_TEXT: Record<string, string> = {
  BAD_BODY: 'dữ liệu gửi lên không hợp lệ — kiểm tra lại rồi thử lại.',
  BAD_FIELDS: 'dữ liệu không hợp lệ — kiểm tra lại rồi thử lại.',
  NOT_FOUND: 'không tìm thấy (có thể đã bị xoá ở nơi khác) — đã tải lại.',
  STALE_BASE: 'diễn xuất vừa đổi ở nơi khác — đã tải lại, xem lại rồi gỡ lại.',
  NOTHING_TO_CLEAR: 'không còn dấu sửa tay nào để gỡ.',
  JOB_RUNNING: 'plan đang có việc chạy ở nền — đợi xong rồi làm lại, đừng bấm lại.',
  NO_DURATION: 'clip chưa có thời lượng nên chưa cắt được.',
  VIDEO_REF_ENGINE: 'video tham chiếu chỉ dùng với Seedance 2.5 — đổi engine của video trước.',
  REF_UNREADABLE: 'không đọc được video tham chiếu — tải lại file khác.',
  NOT_SKY_PLAN: 'plan không dùng luồng mới.',
};
export function manualEditErrorText(err: any, what: string): string {
  const code = err?.response?.data?.code;
  if (typeof code === 'string' && CODE_TEXT[code]) return `${what}: ${CODE_TEXT[code]}`;
  return apiErrorText(err, what);
}
